import { EducationUser, Group, User } from "@microsoft/microsoft-graph-types";
import { Dictionary } from "lodash";
import moment from "moment";
import { Assignment } from "../models/analytics/Assignment";
import { AssignmentsOverTime } from "../models/analytics/AssignmentsOverTime";
import BulkCreateSiteForm, { BulkCreateSiteFormWithFormCount } from "../models/BulkCreateSiteForm";
import CdbApiError from "../models/CdbApiError";
import CreateSiteForm, { CreateSiteTemplate } from "../models/CreateSiteForm";
import DropIn from "../models/DropIn";
import { DurableFunctionHistoryEvent } from "../models/DurableFunctionHistoryEvent";
import { DurableFunctionHttpManagement } from "../models/DurableFunctionHttpManagement";
import {
    DurableFunctionAppName,
    DurableFunctionInstance,
    GroundFloorDurableFunctionInstance
} from "../models/DurableFunctionInstance";
import { MailroomAnalyticsResult } from "../models/MailroomAnalyticsResult";
import MailroomGetPhotosResult from "../models/MailroomGetPhotosResult";
import { ProcessorItem } from "../models/MailroomProcessorItem";
import PagedResult from "../models/PagedResult";
import PagingRequest from "../models/PagingRequest";
import Playlist from "../models/Playlist";
import ProcessorComparisonResult from "../models/ProcessorComparisonResult";
import { SalamanderDatashareRecord } from "../models/SalamanderDatashare";
import School, { License, OnboardStatus, SchoolRef } from "../models/School";
import SchoolAccessRecord from "../models/SchoolAccessRecord";
import SchoolAuditLog from "../models/schoolAuditLog";
import { SchoolDataReport } from "../models/SchoolDataReport";
import SchoolLicense from "../models/SchoolLicense";
import {
    MisDataServiceTypes,
    SalamanderStrategy,
    SchoolMailroomConfigDto,
} from "../models/SchoolMailroomConfigDto";
import { SchoolSchedule } from "../models/SchoolSchedule";
import { SchoolWorkloadConfig } from "../models/SchoolWorkloadConfig";
import SdsSubject from "../models/SdsSubject";
import { GetUploadStatusDTO } from "../models/SdsUploadStatus";
import SdsYear from "../models/SdsYear";
import ServiceAccountCredentials from "../models/ServiceAccountCredentials";
import SharePointApp from "../models/SharePointApp";
import SharePointPackage from "../models/SharePointPackage";
import Subject from "../models/Subject";
import SubjectDataReport from "../models/SubjectDataReport";
import { SubjectMerge } from "../models/SubjectMerge";
import TenantPackageSummary from "../models/TenantPackageSummary";
import UserAccessRecord, { UserAccessLevel } from "../models/UserAccessRecord";
import { WorkloadAnalytics } from "../models/WorkloadAnalytics";
import { WorkloadInfo } from "../models/WorkloadInfo";
import { WorkloadInstanceQuery } from "../models/WorkloadInstanceQuery";
import { MegaMenuOptions } from "./MegaMenuItemSelector";
import { SchoolTheme } from "./pages/onboard/SchoolTheme";
import { SchoolDeleteSettings } from "./SchoolDeleteSettings";
import { StudentAssignment } from "../models/studentAssignmentSearch/StudentAssignment";
import { ResourceInformation } from "../models/studentAssignmentSearch/ResourceInformation";
import { ClassWithSubject } from "../models/studentAssignmentSearch/ClassWithSubject";
import { StagedRolloutSummary } from "../models/StagedRolloutSummary";
import { MailroomAutomationHistoryRecord } from "../models/MailroomAutomationHistoryRecord";
import { SiteExploreResult } from "./pages/admin/explorer/ExploreSitePage";
import { GroupExploreResult } from "./pages/admin/explorer/ExploreGroupPage";
import { SdsInfo } from "../models/SdsInfo";
import { MailroomSchoolConfig } from "../models/MailroomSchoolConfig";
import { TenantRollover } from "../models/rollover/TenantRollover";
import { TenantRolloverState } from "../models/rollover/TenantRolloverState";
import BulkWorkloadItem from "../models/BulkWorkloadItem";
import { RolloverStatisticsRow } from "../models/rollover/RolloverStatisticsRow";
import { SchoolRolloverEvent } from "../models/rollover/SchoolRolloverEvent";
import { IndustryDataRunAndActivityResults } from "../models/industryData/IndustryDataRunAndActivityResults";
import IUsageResponse from "../models/usage-analytics/IUsageResponse";
import ISchoolUsageSummary from "../models/usage-analytics/ISchoolUsageSummary";
import { ISchoolUsageDetail } from "../models/usage-analytics/ISchoolUsageDetail";
import { IUsageArea } from "../models/usage-analytics/ISchoolUsageSummary";
import IUsageMarkScheme from "../models/usage-analytics/IUsageMarkScheme";
import IMisFetchHost from "../models/hosted-salamander/IMisFetchHost";
import IMisFetchRun from "../models/hosted-salamander/IMisFetchRun";
import ISalamanderHostRunBlobFile from "../models/hosted-salamander/IHostRunBlobFile";
import TenantManager from "../models/TenantManager";
import TenantSummary from "../models/tenants/TenantSummary";
import { IMisConnectionDetailsResult } from "../models/mis-connection-details/IMisConnectionDetailsResult";
import { IMisConnectionDetailsPutOperation } from "../models/mis-connection-details/IMisConnectionDetailsPutOperation";
import IHostedSalamanderConfig from "../models/hosted-salamander/IHostedSalamanderConfig";
import IUsageDatasetSummary from "../models/usage-analytics/IUsageDatasetSummary";
import ICDBAppStatus from "../models/ICDBAppStatus";
import { doubleEncodeURIComponent, QueryObject } from "../utils/Utils";
import { StudentAssignmentSearchOutcome } from "../models/studentAssignmentSearch/StudentAssignmentSearchOutcome";
import { IContainerImageTagOverridePatch } from "../models/container-image-tags/ISchoolContainerImageTagOverridePatch";
import { ISchoolContainerImageTagOverride } from "../models/container-image-tags/ISchoolContainerImageTagOverride";
import { Rule } from "../models/mailroom-school-config-rules/Rule";
import {
    BulkProcessorComparisonInput
} from "../models/bulk-processor-comparison/BulkComparisonInput";
import {DriveItemExploreResult} from "./pages/admin/explorer/ExploreDriveItemPage";
import {AvailableLicense} from "../models/AvailableLicense";
import { AssignmentSubject, YearGroupSubmissionSummary } from "../models/analytics/AssignmentSubmissionSummaries";
import StudentSubmissionResponse from "../models/analytics/StudentSubmissionResponse";
import TeacherSubmissionResponse from "../models/analytics/TeacherSubmissionResponse";
import SubjectsSubmissionResponse from "../models/analytics/SubjectsSubmissionResponse";
import { AssignmentFetchStatus } from "../models/analytics/AssignmentFetchStatus";
import SubjectSubmissionResponse from "../models/analytics/SubjectSubmissionResponse";
import ClassSubmissionResponse from "../models/analytics/ClassSubmissionResponse";
import {UserExploreResult} from "./pages/admin/explorer/ExploreUserPage";
import AssignmentAnalyticsOverviewItem from "../models/analytics/monitoring/AssignmentAnalyticsOverviewItem";
import {TotpStatus} from "../models/tenants/TotpStatus";
import SchoolAssignmentsResult from "../models/analytics/SchoolAssignmentsResult";
import {SchoolOverviewResult} from "../models/analytics/SchoolOverviewResult";
import AdminRolloverForm from "../models/rollover/AdminRolloverForm";
import RolloverConfirmForm from "../models/rollover/RolloverConfirmForm";
import {RolloverSummary} from "../models/rollover/RolloverSummary";
import {SchoolsCanAccessResponse} from "../models/SchoolsCanAccessResponse";

interface RequestOptions {
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    queryParams?: { [key: string]: string },
    contentType?: "application/json" | "application/xml" | "none" | "text/csv" | "text/plain" | "application/zip" | "application/octet-stream",
    body?: any,
    abortController?: AbortController
}


export interface AccessTokenProvider {
    getAccessToken(): Promise<string>
}

export default class ApiService {
    private accessTokenProvider: AccessTokenProvider;

    constructor(accessTokenProvider: AccessTokenProvider) {
        this.accessTokenProvider = accessTokenProvider;
    }

    public static buildQueryString(params: { [key: string]: any }) {

        return Object.keys(params).map((key) => {
            let value = params[key];
            if (value !== undefined && value !== null) {
                if (value instanceof Array) {
                    return value.map(v => `${key}=${v}`).join("&");
                }
                if (value instanceof Date) {
                    value = moment(value).format("YYYY-MM-DD HH:mm");
                }
                return `${key}=${value}`;
            }
            return "";
        }).filter((v) => v !== "").join("&");
    }

    private async authenticatedReq(url: string, options: RequestOptions, forceReload: boolean = false): Promise<any> {

        var requestBody = undefined;
        if (options.body !== undefined) {
            if (typeof options.body === "string") {
                requestBody = options.body;
            }
            else if (options.body instanceof FormData) {
                requestBody = options.body;
            }
            else {
                requestBody = JSON.stringify(options.body);
            }
        }

        const queryString = options.queryParams ? `?${ApiService.buildQueryString(options.queryParams)}` : '';
        const fullUrl = `${url}${queryString}`;

        const token = await this.accessTokenProvider.getAccessToken();
        const headers = {
            'Content-Type': options.contentType ? options.contentType : 'application/json',
            'Authorization': `Bearer ${token}`
        }

        // Removes content-type to allow multipart/form-data with no boundaries
        if (options.contentType == "none") {
            delete headers['Content-Type'];
        }

        const response = await fetch(fullUrl, {
            method: options.method,
            headers: headers,
            body: requestBody,
            signal: options.abortController?.signal,
            cache: forceReload ? "reload" : "default",
        });


        const responseText = await response.text();
        let responseObject: any = undefined;
        if (responseText && responseText !== '') {
            try {
                responseObject = JSON.parse(responseText);
            }
            catch (e) {
            }
        }

        if (response.status.toString()[0] !== "2") {
            await this.handleErrorResponse(response, responseText);
        }

        if (responseObject !== undefined) {
            return responseObject;
        }
        else {
            return responseText;
        }
    }

    private async handleErrorResponse(response: Response, responseText: string) {
        let responseObject: any;
        if (responseText && responseText !== '') {
            try {
                responseObject = JSON.parse(responseText);
            }
            catch (e) {
            }
        }

        let cdbApiError = responseObject as CdbApiError;
        if (cdbApiError === undefined) {
            const error = new Error(responseText);
            console.log(error);
            throw error;
        }
        else {
            cdbApiError.status = response.status;
            console.log(cdbApiError);
            throw cdbApiError;
        }
    }

    private async authenticatedReqBlob(url: string, options: RequestOptions): Promise<Response | undefined> {

        var requestBody = undefined;
        if (options.body !== undefined) {
            if (typeof options.body === "string") {
                requestBody = options.body;
            }
            else if (options.body instanceof FormData) {
                requestBody = options.body;
            }
            else {
                requestBody = JSON.stringify(options.body);
            }
        }

        const queryString = options.queryParams ? `?${ApiService.buildQueryString(options.queryParams)}` : '';
        const fullUrl = `${url}${queryString}`;

        const token = await this.accessTokenProvider.getAccessToken();
        const headers = {
            'Content-Type': options.contentType ? options.contentType : 'application/json',
            'Authorization': `Bearer ${token}`
        }

        // Removes content-type to allow multipart/form-data with no boundaries
        if (options.contentType == "none") {
            delete headers['Content-Type'];
        }

        const response = await fetch(fullUrl, {
            method: options.method,
            headers: headers,
            body: requestBody
        });

        if (response.ok) {
            return response;
        } else {
            const responseText = await response.text();
            await this.handleErrorResponse(response, responseText);
        }
    }


    private async authenticatedGet(url: string, queryParams?: { [key: string]: any }, controller?: AbortController, forceReload: boolean = false): Promise<any> {
        return await this.authenticatedReq(url, {
            queryParams: queryParams,
            method: "GET",
            abortController: controller
        },
            forceReload
        );
    }

    private async authenticatedPost(url: string, body: any): Promise<any> {
        return await this.authenticatedReq(url, {
            body: body,
            method: "POST"
        });
    }

    private async authenticatedPut(url: string, body: any): Promise<any> {
        return await this.authenticatedReq(url, {
            body: body,
            method: "PUT"
        });
    }

    private async authenticatedPatch(url: string, body: any): Promise<any> {
        return await this.authenticatedReq(url, {
            body: body,
            method: "PATCH"
        });
    }

    private async authenticatedDelete(url: string, body: any): Promise<any> {
        return await this.authenticatedReq(url, {
            body: body,
            method: "DELETE"
        });
    }

    public async getTenantPackageSummariesAdmin(): Promise<TenantPackageSummary[]> {
        return await this.authenticatedGet('admin/tenant-sharepoint-packages');
    }
    public async getTenantPackageSummaryAdmin(tenantId: string): Promise<TenantPackageSummary> {
        return await this.authenticatedGet(`admin/tenant-sharepoint-packages/${tenantId}`);
    }
    public async getTenantSchoolsAdmin(tenantId: string): Promise<School[]> {
        return await this.authenticatedGet(`admin/tenant-sharepoint-packages/${tenantId}/schools`);
    }
    public async getTenantSharePointAppsAdmin(tenantId: string): Promise<SharePointApp[]> {
        return await this.authenticatedGet(`admin/tenant-sharepoint-packages/${tenantId}/sharepointapps`);
    }
    public async addOrUpdateSharePointAppsAdmin(tenantId: string, apps: SharePointApp[]): Promise<SharePointApp[]> {
        return await this.authenticatedPost(`admin/tenant-sharepoint-packages/${tenantId}/sharepointapps/update`, apps);
    }

    public async applySharePointAppToTenants(sharePointPackage: SharePointPackage, tenantIds: string[]): Promise<boolean> {
        return await this.authenticatedPost(`admin/tenant-sharepoint-packages/sharepointapps/apply`, { sharePointPackage, tenantIds });
    }

    public async startWorkloadForTenants(tenantIds: string[], workloadName: string): Promise<any> {
        return await this.authenticatedPost(`admin/tenant-sharepoint-packages/workload/${workloadName}/start`, tenantIds);
    }

    public async getAvailableSharePointPackages(): Promise<SharePointPackage[]> {
        return await this.authenticatedGet(`admin/sharepointpackages`);
    }

    public async getTenantsAdmin(): Promise<TenantSummary[]> {
        return await this.authenticatedGet(`admin/tenants`);
    }

    public async getTenantAdmin(tenantId: string): Promise<TenantSummary | null> {
        return await this.authenticatedGet(`admin/tenants/${tenantId}`);
    }

    public async getAppRoleAssignmentsForTenantAdmin(tenantId: string): Promise<any[]> {
        return await this.authenticatedGet(`admin/tenants/${tenantId}/app-role-assignments`)
    }

    public async getDelegatedAppPermissionsForTenantAdmin(tenantId: string): Promise<any[]> {
        return await this.authenticatedGet(`admin/tenants/${tenantId}/delegated-app-permissions`)
    }

    public async getSchoolsForTenantAdmin(tenantId: string): Promise<School[]> {
        return await this.authenticatedGet(`admin/tenants/${tenantId}/schools`)
    }

    public async getSchoolsCanAccess(): Promise<SchoolsCanAccessResponse> {
        return await this.authenticatedGet('schools/can-access');
    }

    public async getSchool(tenantId: string, schoolCode: string, forceReload: boolean = false): Promise<School> {
        return await this.authenticatedGet(`schools/${tenantId}/${schoolCode}`,{},undefined,forceReload)
    }


    public async getSchoolsAdmin(showDisabled: boolean, showExpired: boolean, showOnlyThirdPartySds: boolean, licenses: License[], onboardStatuses: OnboardStatus[], filterString: string): Promise<School[]> {
        const queryString = this.getSchoolsQueryString(showDisabled, showExpired, showOnlyThirdPartySds, licenses, onboardStatuses, filterString)
        return await this.authenticatedGet(`admin/schools?${queryString}`);
    }

    public async downloadSchoolsQuery(showDisabled: boolean, showExpired: boolean, showOnlyThirdPartySds: boolean, licenses: License[], onboardStatuses: OnboardStatus[], filterString: string): Promise<Response | undefined> {
        const queryString = this.getSchoolsQueryString(showDisabled, showExpired, showOnlyThirdPartySds, licenses, onboardStatuses, filterString)
        const url = `admin/schools/csv?${queryString}`;

        return await this.authenticatedReqBlob(url, {
            method: "GET",
            contentType: "text/csv",
        });
    }

    private getSchoolsQueryString(showDisabled: boolean, showExpired: boolean, showOnlyThirdPartySds: boolean, licenses: License[], onboardStatuses: OnboardStatus[], filterString: string): string {
        const licenseQueryParams = licenses.map(l => `license=${l}`);
        const onboardStatusQueryParams = onboardStatuses.map(o => `onboardStatus=${o}`);
        const queryParams = [`showDisabled=${showDisabled}`, `showExpired=${showExpired}`, `showOnlyThirdPartySds=${showOnlyThirdPartySds}`, `filterString=${filterString}`, ...licenseQueryParams, ...onboardStatusQueryParams];
        return queryParams.join("&")
    }

    public async getSchoolAdmin(tenantId: string, schoolCode: string, forceReload: boolean = false): Promise<School> {
        return await this.authenticatedGet(`admin/schools/${tenantId}/${schoolCode}`, {}, undefined, forceReload);
    }

    public async getSchoolDeleteSettingsAdmin(): Promise<SchoolDeleteSettings> {
        return await this.authenticatedGet(`admin/schools/deleteSettings`);
    }

    public async enableSchoolAdmin(tenantId: string, schoolCode: string): Promise<School> {
        return await this.authenticatedPut(`admin/schools/${tenantId}/${schoolCode}/enable`, {});
    }

    public async disableSchoolAdmin(tenantId: string, schoolCode: string): Promise<School> {
        return await this.authenticatedPut(`admin/schools/${tenantId}/${schoolCode}/disable`, {});
    }
    public async deleteSchoolAdmin(tenantId: string, schoolCode: string): Promise<void> {
        return await this.authenticatedPut(`admin/schools/${tenantId}/${schoolCode}/delete`, {});
    }

    public async getSdsSubjectsForSchool(tenantId: string, schoolCode: string): Promise<SdsSubject[]> {
        return await this.authenticatedGet(`admin/schools/${tenantId}/${schoolCode}/sds-subjects`);
    }

    public async getSdsYearsForSchool(tenantId: string, schoolCode: string): Promise<SdsYear[]> {
        return await this.authenticatedGet(`admin/schools/${tenantId}/${schoolCode}/sds-years`);
    }

    public async getSchoolsInTenant(): Promise<School[]> {
        return await this.authenticatedGet('onboard/schools-in-tenant');
    }

    public async getSubjectsForDataPage(tenantId: string, schoolCode: string, academicYear: number): Promise<SubjectDataReport> {
        const academicYearString = String(academicYear);
        return await this.authenticatedGet(`schools/${tenantId}/${schoolCode}/schoolSubjectData`, {
            academicYear: academicYearString
        });
    }

    public async updateSchoolAdmin(school: School): Promise<School> {
        return await this.authenticatedPut('admin/schools', school);
    }

    public async getLicenseUpdateMethod(tenantId: string): Promise<"update" | "assign"> {
        return await this.authenticatedGet(`admin/license/update-method/${tenantId}`)
    }

    public async updateLicenseAdmin(schoolLicense: SchoolLicense): Promise<void> {
        return await this.authenticatedPut('admin/license', schoolLicense);
    }

    public async getLicenseAdmin(tenantId: string, schoolCode: string): Promise<SchoolLicense> {
        return await this.authenticatedGet(`admin/license/${tenantId}/${schoolCode}`);
    }

    public async getAvailableLicenses(): Promise<AvailableLicense[]> {
        return await this.authenticatedGet(`admin/license/available`);
    }

    public async assignAvailableLicense(tenantId: string, schoolCode: string, licenseReference: string): Promise<void> {
        return await this.authenticatedPost(`admin/license/${tenantId}/${schoolCode}/assign-available/${licenseReference}`, "");
    }

    public async unassignLicense(tenantId: string, schoolCode: string): Promise<void> {
        return await this.authenticatedPost(`admin/license/${tenantId}/${schoolCode}/unassign`, "");
    }

    public async assignTemporaryLicense(tenantId: string, schoolCode: string, license: License): Promise<void> {
        return await this.authenticatedPost(`admin/license/${tenantId}/${schoolCode}/assign-temporary/${license}`, "");
    }

    public async searchGraphUsersAdmin(tenantId: string, filter: string): Promise<User[]> {
        return await this.authenticatedGet(`admin/graph/${tenantId}/search/users`, { filter })
    }

    public async searchGraphGroupsAdmin(tenantId: string, filter: string): Promise<Group[]> {
        return await this.authenticatedGet(`admin/graph/${tenantId}/search/groups`, { filter })
    }

    public async updateSchoolLogoAdmin(tenantId: string, schoolCode: string, file: File): Promise<any> {
        let formData = new FormData();
        formData.append('file', file);
        return await this.authenticatedReq(`admin/schools/${tenantId}/${schoolCode}/logo`, {
            method: "POST",
            contentType: "none",
            body: formData
        });
    }

    public async forceOnboardStatus(tenantId: string, schoolCode: string, status: OnboardStatus): Promise<School> {
        return await this.authenticatedPost(`onboard/${tenantId}/${schoolCode}/force-onboard-status/${status}`, "");
    }

    public async moveToNextOnboardingState(tenantId: string, schoolCode: string): Promise<School> {
        return await this.authenticatedPost(`onboard/${tenantId}/${schoolCode}/next-onboarding-state`, "");
    }


    public async postCreateSiteForm(form: CreateSiteForm): Promise<CreateSiteForm> {
        return await this.authenticatedPost('forms/create-site', form)
    }

    public async getCreateSiteForms(tenantId: string, schoolCode: string, siteType: string): Promise<CreateSiteForm[]> {
        return await this.authenticatedGet(`forms/create-site/${tenantId}/${schoolCode}`, { siteType });
    }

    public async getCreateSiteTemplates(tenantId: string, schoolCode: string): Promise<CreateSiteTemplate[]> {
        return await this.authenticatedGet(`forms/create-site/${tenantId}/${schoolCode}/templates`)
    }

    public async postDropIns(dropIns: DropIn[]): Promise<DropIn[]> {
        return await this.authenticatedPost('/drop-ins', dropIns)
    }

    public async getDropIns(tenantId: string, schoolCode: string): Promise<DropIn[]> {
        return await this.authenticatedGet('/drop-ins', { tenantId, schoolCode });
    }

    public async putBulkCreateSiteForm(form: BulkCreateSiteForm): Promise<BulkCreateSiteForm> {
        return await this.authenticatedPut('forms/create-site/bulk', form);
    }

    public async getBulkCreateSiteForms(tenantId: string, schoolCode: string): Promise<BulkCreateSiteFormWithFormCount[]> {
        return await this.authenticatedGet('forms/create-site/bulk', { tenantId, schoolCode });
    }

    public async getBulkCreateSiteForm(bulkSiteFormId: string): Promise<BulkCreateSiteForm> {
        return await this.authenticatedGet(`forms/create-site/bulk/${bulkSiteFormId}`);
    }

    public async approveBulkCreateSiteForm(bulkSiteFormId: string): Promise<BulkCreateSiteForm> {
        return await this.authenticatedPost(`forms/create-site/bulk/${bulkSiteFormId}/approve`, "")
    }

    public async getWorkloadInstancesForBulkSiteForm(bulkSiteFormId: string): Promise<GroundFloorDurableFunctionInstance[]> {
        return await this.authenticatedGet(`forms/create-site/bulk/${bulkSiteFormId}/workloads`);
    }

    public async getGroundFloorDurableFunctionInstancesForIds(ids: string[]): Promise<GroundFloorDurableFunctionInstance[]> {
        return await this.authenticatedPost(`admin/workloads/proxy/workloads/instances/ids`, { ids: ids });
    }

    public async getAccessRecordsForSchool(tenantId: string, schoolCode: string): Promise<SchoolAccessRecord[]> {
        return await this.authenticatedGet('school-access', {
            tenantId,
            schoolCode
        });
    }

    public async createSchoolAccessRecord(accessRecord: SchoolAccessRecord): Promise<void> {
        return await this.authenticatedPost('school-access', accessRecord);
    }

    public async deleteAccessRecord(accessRecord: SchoolAccessRecord): Promise<void> {
        return await this.authenticatedDelete('school-access', accessRecord);
    }


    public async getCurrentUserAccess(): Promise<UserAccessLevel> {
        return await this.authenticatedGet('admin/user-access/current');
    }
    public async getUserAccessRecords(): Promise<UserAccessRecord[]> {
        return await this.authenticatedGet('admin/user-access');
    }
    public async createUserAccessRecord(accessRecord: UserAccessRecord): Promise<void> {
        return await this.authenticatedPost('admin/user-access', accessRecord);
    }
    public async deleteUserAccessRecord(accessRecord: UserAccessRecord): Promise<void> {
        return await this.authenticatedDelete('admin/user-access', accessRecord);
    }

    public async getSubjectMergesForSchool(tenantId: string, schoolCode: string): Promise<SubjectMerge[]> {
        return await this.authenticatedGet(`admin/subject-merges/${tenantId}/${schoolCode}`)
    }

    public async putSubjectMergesForSchool(tenantId: string, schoolCode: string, subjectMerges: SubjectMerge[]): Promise<SubjectMerge[]> {
        return await this.authenticatedPut(`admin/subject-merges/${tenantId}/${schoolCode}`, subjectMerges)
    }

    public async getSubjectsForSchool(tenantId: string, schoolCode: string, academicYear: number): Promise<Subject[]> {
        const academicYearString = String(academicYear);
        return await this.authenticatedGet('subjects', {
            tenantId: tenantId,
            schoolCode: schoolCode,
            academicYear: academicYearString
        });
    }

    public async previewSubjectsInMailroomInbox(tenantId: string, schoolCode: string, overrides?: Partial<MailroomSchoolConfig>): Promise<SdsSubject[]> {
        return await this.authenticatedPost(`admin/mailroom/proxy/salamander/processor/${tenantId}/${schoolCode}/previewSubjectsInInbox`, overrides);
    }
    
    public async getAssignmentAnalyticsForTenant(tenantId: string, startDate: string, endDate: string, academicYear: number): Promise<SchoolAssignmentsResult[]> {
        const academicYearString = String(academicYear);
        return await this.authenticatedGet(`assignment-analytics/${tenantId}`, {
            startDate,
            endDate,
            academicYear: academicYearString
        });
    }


    private searchStudentAssignmentsAbortController?: AbortController;
    public async searchStudentAssignments(tenantId: string, schoolCode: string, userId: string): Promise<Assignment[]> {

        if (this.searchStudentAssignmentsAbortController) {
            this.searchStudentAssignmentsAbortController.abort();
        }

        this.searchStudentAssignmentsAbortController = new AbortController();

        return await this.authenticatedGet(`/student-assignment-search/${tenantId}/${schoolCode}/search/user/${userId}`, {}, this.searchStudentAssignmentsAbortController);
    }

    public async checkUserCanAccessAssignmentSearch(tenantId: string, schoolCode: string): Promise<void> {
        return await this.authenticatedGet(`/student-assignment-search/${tenantId}/${schoolCode}/can-access`);
    }

    public async getAssignment(tenantId: string, schoolCode: string, classId: string, assignmentId: string, userId: string): Promise<StudentAssignment> {
        return await this.authenticatedGet(`/student-assignment-search/${tenantId}/${schoolCode}/search/class/${classId}/assignments/${assignmentId}/users/${userId}`)
    }

    public async getSubmissionResourcesForAssignmentSubmission(tenantId: string, schoolCode: string, assignmentId: string, classId: string, submissionId: string): Promise<ResourceInformation[]> {
        return await this.authenticatedGet(`/student-assignment-search/${tenantId}/${schoolCode}/search/class/${classId}/assignments/${assignmentId}/submission/${submissionId}`);
    }

    public async getOutcomeForAssignmentSubmission(tenantId: string, schoolCode: string, assignmentId: string, classId: string, submissionId: string): Promise<StudentAssignmentSearchOutcome> {
        return await this.authenticatedGet(`/student-assignment-search/${tenantId}/${schoolCode}/search/class/${classId}/assignments/${assignmentId}/submission/${submissionId}/outcome`);
    }

    public async getClassWithSubject(tenantId: string, schoolCode: string, classId: string): Promise<ClassWithSubject> {
        return await this.authenticatedGet(`/student-assignment-search/${tenantId}/${schoolCode}/search/class/${classId}`)
    }

    public async getUser(tenantId: string, schoolCode: string, userId: string): Promise<EducationUser> {
        return await this.authenticatedGet(`/student-assignment-search/${tenantId}/${schoolCode}/search/student/${userId}`);
    }

    public async downloadEducationSubmissionResource(tenantId: string, schoolCode: string, assignmentId: string, classId: string, submissionId: string, resourceId: string): Promise<Response | undefined> {
        const url = `/student-assignment-search/${tenantId}/${schoolCode}/class/${classId}/assignments/${assignmentId}/submission/${submissionId}/resource/${resourceId}`;
        return await this.authenticatedReqBlob(url, {
            method: "GET",
            contentType: "application/octet-stream",
        });
    }

    public async downloadEducationAssignmentResource(tenantId: string, schoolCode: string, assignmentId: string, classId: string, resourceId: string): Promise<Response | undefined> {
        const url = `/student-assignment-search/${tenantId}/${schoolCode}/class/${classId}/assignments/${assignmentId}/resource/${resourceId}`;
        return await this.authenticatedReqBlob(url, {
            method: "GET",
            contentType: "application/octet-stream",
        });
    }

    public async getAssignmentsOverTimeForSchool(tenantId: string, schoolCode: string, startDate: string, endDate: string, academicYear: number): Promise<AssignmentsOverTime> {
        const academicYearString = String(academicYear);
        return await this.authenticatedGet(`assignment-analytics/${tenantId}/${schoolCode}/over-time`, {
            startDate,
            endDate,
            academicYear: academicYearString
        });
    }
    

    public async getCsvExport(tenantId: string, schoolCode: string, startDate: string, endDate: string, academicYear: number): Promise<Response | undefined> {
        const academicYearString = String(academicYear);
        const url = `assignment-analytics/${tenantId}/${schoolCode}/export`;
        return await this.authenticatedReqBlob(url, {
            method: "GET",
            contentType: "text/csv",
            queryParams: {
                startDate,
                endDate,
                academicYear: academicYearString
            }
        });
    }

    public async listAssignmentSubjects(tenantId: string, schoolCode: string, academicYear: number): Promise<AssignmentSubject[]> {
        return await this.authenticatedGet(`assignment-analytics/${tenantId}/${schoolCode}/submissions/${academicYear}/subjects/list`);
    }

    public async getStudentSubmissionsAnalyticsForSchool(tenantId: string, schoolCode: string, academicYear: number, startDate: string, endDate: string): Promise<StudentSubmissionResponse> {
        return await this.authenticatedGet(`assignment-analytics/${tenantId}/${schoolCode}/submissions/${academicYear}/students`, {
            startDate,
            endDate,
        });
    }

    public async getTeacherSubmissionsAnalyticsForSchool(tenantId: string, schoolCode: string, academicYear: number, startDate: string, endDate: string, subject: string): Promise<TeacherSubmissionResponse> {
        return await this.authenticatedGet(`assignment-analytics/${tenantId}/${schoolCode}/submissions/${academicYear}/teachers`, {
            startDate,
            endDate,
            subject
        });
    }

    public async getYearGroupSubmissionsAnalyticsForSchool(tenantId: string, schoolCode: string, academicYear: number, startDate: string, endDate: string): Promise<YearGroupSubmissionSummary[]> {
        return await this.authenticatedGet(`assignment-analytics/${tenantId}/${schoolCode}/submissions/${academicYear}/yeargroups`, {
            startDate,
            endDate,
        });
    }

    public async getSubjectsSubmissionsAnalyticsForSchool(tenantId: string, schoolCode: string, academicYear: number, startDate: string, endDate: string): Promise<SubjectsSubmissionResponse> {
        return await this.authenticatedGet(`assignment-analytics/${tenantId}/${schoolCode}/submissions/${academicYear}/subjects`, {
            startDate,
            endDate,
        });
    }

    public async getClassSubmissionAnalytics(tenantId: string, schoolCode: string, academicYear: number, startDate: string, endDate: string, classId: string): Promise<ClassSubmissionResponse> {
        return await this.authenticatedGet(`assignment-analytics/${tenantId}/${schoolCode}/submissions/${academicYear}/classes/${classId}`, {
        startDate,
        endDate
        });
    }

    public async getSubjectSubmissionAnalytics(tenantId: string, schoolCode: string, academicYear: number, startDate: string, endDate: string,subjectCode: string): Promise<SubjectSubmissionResponse>
    {
        return await this.authenticatedGet(`assignment-analytics/${tenantId}/${schoolCode}/submissions/${academicYear}/subjects/${subjectCode}`, {
            startDate,
            endDate,
        });
    }
    
    public async getStudentSubmissionsAnalyticsForSchoolExport(tenantId: string, schoolCode: string, academicYear: number, startDate: string, endDate: string): Promise<Response | undefined> {
        return await this.authenticatedReqBlob(`assignment-analytics/${tenantId}/${schoolCode}/submissions/${academicYear}/students/export`, {
            method: "GET",
            contentType: "text/csv",
            queryParams: {
                startDate,
                endDate,
            }
        });
    }

    public async getTeacherSubmissionsAnalyticsForSchoolExport(tenantId: string, schoolCode: string, academicYear: number, startDate: string, endDate: string, subject: string): Promise<Response | undefined> {
        return await this.authenticatedReqBlob(`assignment-analytics/${tenantId}/${schoolCode}/submissions/${academicYear}/teachers/export`, {
            method: "GET",
            contentType: "text/csv",
            queryParams: {
                startDate,
                endDate,
                subject
            }
        });
    }

    public async getYearGroupSubmissionsAnalyticsForSchoolExport(tenantId: string, schoolCode: string, academicYear: number, startDate: string, endDate: string): Promise<Response | undefined> {
        return await this.authenticatedReqBlob(`assignment-analytics/${tenantId}/${schoolCode}/submissions/${academicYear}/yeargroups/export`, {
            method: "GET",
            contentType: "text/csv",
            queryParams: {
                startDate,
                endDate,
            }
        });
    }

    public async getSubjectsSubmissionsAnalyticsForSchoolExport(tenantId: string, schoolCode: string, academicYear: number, startDate: string, endDate: string): Promise<Response | undefined> {
        return await this.authenticatedReqBlob(`assignment-analytics/${tenantId}/${schoolCode}/submissions/${academicYear}/subjects/export`, {
            method: "GET",
            contentType: "text/csv",
            queryParams: {
                startDate,
                endDate,
            }
        });
    }
    
    public async getSchoolAnalyticsOverviewResult(tenantId: string, schoolCode: string, startDate: string, endDate: string, academicYear: string): Promise<SchoolOverviewResult> {
        return await this.authenticatedGet(`/assignment-analytics/${tenantId}/${schoolCode}`, {
            academicYear,
            startDate,
            endDate,
        });
    }

    public async getSchoolAssignmentFetchStatus(tenantId: string, schoolCode: string, academicYear: number): Promise<AssignmentFetchStatus> {
        return await this.authenticatedGet(`assignment-analytics/${tenantId}/${schoolCode}/fetch-status/${academicYear}`);
    }
    
    public async getAssignmentAnalyticsMonitoringOverview(academicYear: number) : Promise<AssignmentAnalyticsOverviewItem[]> {
        return await this.authenticatedGet(`assignment-analytics-monitoring/overview/${academicYear}`)
    }

    private workloadInstancesAbortController?: AbortController;
    public async getGroundFloorDurableFunctionInstances(workloadInstanceQuery: WorkloadInstanceQuery): Promise<GroundFloorDurableFunctionInstance[]> {

        if (this.workloadInstancesAbortController) {
            this.workloadInstancesAbortController.abort();
        }
        this.workloadInstancesAbortController = new AbortController();
        return await this.authenticatedGet("admin/workloads/proxy/workloads/instances", workloadInstanceQuery, this.workloadInstancesAbortController)
    }

    public async getGroundFloorDurableFunctionInstancesDownload(workloadInstanceQuery: WorkloadInstanceQuery): Promise<any> {

        var url = "admin/workloads/proxyFileResult/workloads/instances/download";

        return await this.authenticatedReqBlob(url, {
            method: "GET",
            contentType: "application/zip",
            queryParams: workloadInstanceQuery,
            abortController: this.workloadInstancesAbortController
        });
    }

    public async getGroundFloorDurableFunctionInstance(instanceId: string): Promise<GroundFloorDurableFunctionInstance> {
        return await this.authenticatedGet(`admin/workloads/proxy/workloads/instances/${instanceId}`)
    }

    public async getGroundFloorDurableFunctionInstanceChildren(instanceId: string): Promise<GroundFloorDurableFunctionInstance[]> {
        return await this.authenticatedGet(`admin/workloads/proxy/workloads/instances/${instanceId}/children`)
    }

    public async getGroundFloorDurableFunctionInstanceChildrenDownload(instanceId: string): Promise<Response | undefined> {
        var url = `admin/workloads/proxyFileResult/workloads/instances/${instanceId}/children/download`;

        return await this.authenticatedReqBlob(url, {
            method: "GET",
            contentType: "application/zip",
        });
    }

    public async getWorkloads(): Promise<WorkloadInfo[]> {
        return await this.authenticatedGet(`admin/workloads/proxy/workloads`)
    }

    public async getPlaylists(): Promise<Playlist[]> {
        return await this.authenticatedGet(`admin/workloads/proxy/playlists`)
    }

    public async getStagedRolloutSummaries(): Promise<StagedRolloutSummary[]> {
        return await this.authenticatedGet(`admin/staged-rollouts`);
    }

    public async getWorkloadAnalytics(startDate: string, endDate: string): Promise<WorkloadAnalytics> {
        return await this.authenticatedGet(`admin/workloads/proxy/workloads/analytics`, {
            startDate,
            endDate
        });
    }

    public async getSchoolSchedules(school: School): Promise<SchoolSchedule[]> {
        return await this.authenticatedGet(`admin/workloads/proxy/workloads/schedules`, {
            tenantName: school.tenantName?.toLowerCase()!,
            schoolCode: school.schoolCode?.toLowerCase()!
        });
    }

    public async putSchoolSchedule(schedule: SchoolSchedule): Promise<string> {
        const url = `admin/workloads/proxy/tenant/${schedule.tenantName}/school/${schedule.schoolCode}/workload/${schedule.name}/schedule`;
        return await this.authenticatedPut(url, schedule);
    }

    public async deleteSchoolSchedule(schedule: SchoolSchedule): Promise<void> {
        const url = `admin/workloads/proxy/tenant/${schedule.tenantName}/school/${schedule.schoolCode}/workload/${schedule.name}/schedule`;
        return await this.authenticatedDelete(url, "");
    }

    public async triggerWorkload(school: School, workloadName: string): Promise<GroundFloorDurableFunctionInstance> {
        const url = `admin/workloads/proxy/tenant/${school.tenantName}/school/${school.schoolCode}/workload/${workloadName}/trigger`;
        return await this.authenticatedPost(url, "");
    }

    public async triggerWorkloadWithInput(tenantName: string, schoolCode: string, workloadName: string, input: any): Promise<GroundFloorDurableFunctionInstance> {
        const url = `admin/workloads/proxy/tenant/${tenantName}/school/${schoolCode}/workload/${workloadName}/trigger`;
        return await this.authenticatedPost(url, input);
    }

    public async triggerBulkWorkload(command: { bulkWorkloadName: string, schools: SchoolRef[], startPaused: boolean }): Promise<GroundFloorDurableFunctionInstance> {
        const url = `admin/workloads/proxy/TriggerBulkWorkload`;
        return await this.authenticatedPost(url, command);
    }

    public async getBulkWorkloadItems(bulkInstanceId: string): Promise<BulkWorkloadItem[]> {
        const url = `admin/workloads/proxy/workloads/bulk/${bulkInstanceId}/items`;
        return await this.authenticatedGet(url);
    }

    public async bulkWorkloadItemStartAnother(runBulkWorkloadItemInstanceId: string, schoolCode: string): Promise<any> {
        const url = `admin/workloads/proxy/workloads/bulk/item/${runBulkWorkloadItemInstanceId}/StartAnother/${schoolCode}`;
        return await this.authenticatedPost(url, null);
    }

    public async bulkWorkloadItemExclude(runBulkWorkloadItemInstanceId: string, schoolCode: string): Promise<any> {
        const url = `admin/workloads/proxy/workloads/bulk/item/${runBulkWorkloadItemInstanceId}/Exclude/${schoolCode}`;
        return await this.authenticatedPost(url, null);
    }

    public async bulkWorkloadItemBegin(runBulkWorkloadItemInstanceId: string): Promise<any> {
        const url = `admin/workloads/proxy/workloads/bulk/item/${runBulkWorkloadItemInstanceId}/Begin`;
        return await this.authenticatedPost(url, null);
    }

    public async bulkWorkloadStartAnotherWithFailed(bulkInstanceId: string): Promise<GroundFloorDurableFunctionInstance> {
        const url = `admin/workloads/proxy/workloads/bulk/${bulkInstanceId}/startAnotherWithFailed`;
        return await this.authenticatedPost(url, {});
    }

    public async triggerPlaylist(school: School, playlistName: string): Promise<GroundFloorDurableFunctionInstance> {
        const url = `admin/workloads/proxy/tenant/${school.tenantName}/school/${school.schoolCode}/playlist/${playlistName}/trigger`;
        return await this.authenticatedPost(url, "");
    }

    public async getWorkUnitLogs(workloadInstanceId: string, workUnitInvocationId: string): Promise<string> {
        const url = `admin/workloads/proxy/workloads/instances/${workloadInstanceId}/${workUnitInvocationId}/logs`;
        return await this.authenticatedGet(url);
    }

    public async terminateGroundFloorDurableFunctionInstance(instanceId: string): Promise<void> {
        const url = `admin/workloads/proxy/instances/${instanceId}/terminate`;
        return await this.authenticatedPost(url, "");
    }

    public async restartPlaylist(instanceId: string): Promise<void> {
        const url = `admin/workloads/playlists/${instanceId}/restart`;
        return await this.authenticatedPost(url, "");
    }

    public async rewindGroundFloorInstancePreview(instanceId: string): Promise<void> {
        const url = `admin/workloads/RewindInstancePreview/${instanceId}`;
        return await this.authenticatedPost(url, "");
    }

    public async getMegaMenuOptions(tenantId: string, schoolCode: string): Promise<MegaMenuOptions> {
        const url = `mega-menu/${tenantId}/${schoolCode}/options`;
        return await this.authenticatedGet(url);
    }

    public async putMegaMenuOptions(tenantId: string, schoolCode: string, megaMenuOptions: MegaMenuOptions): Promise<MegaMenuOptions> {
        const url = `mega-menu/${tenantId}/${schoolCode}/options`;
        return await this.authenticatedPut(url, megaMenuOptions);
    }

    public async getSchoolTheme(tenantId: string, schoolCode: string): Promise<SchoolTheme> {
        const url = `school-theme/${tenantId}/${schoolCode}/schooltheme`;
        return await this.authenticatedGet(url);
    }

    public async putSchoolTheme(tenantId: string, schoolCode: string, schoolTheme: SchoolTheme): Promise<SchoolTheme> {
        const url = `school-theme/${tenantId}/${schoolCode}/schooltheme`;
        return await this.authenticatedPut(url, schoolTheme);
    }

    public async getSchoolLogo(tenantId: string, schoolCode: string): Promise<File | undefined> {
        const url = `school-logo/${tenantId}/${schoolCode}/schoollogo`;
        let response = await this.authenticatedReqBlob(url,
            {
                method: "GET"
            });

        if (response !== undefined) {
            return (await response.blob()) as any;
        }

        return undefined;
    }

    public async putSchoolLogo(tenantId: string, schoolCode: string, file: File): Promise<any> {
        let formData = new FormData();
        formData.append('file', file);
        return await this.authenticatedReq(`school-logo/${tenantId}/${schoolCode}/schoollogo`, {
            method: "PUT",
            contentType: "none",
            body: formData
        });
    }

    public async getSchoolConfigXml(school: School): Promise<string> {
        const url = `admin/schools/${school.tenantId}/${school.schoolCode}/config`;
        return await this.authenticatedReq(url, {
            method: "GET",
            contentType: "application/xml"
        });
    }

    public async putSchoolConfigXml(school: School, config: string): Promise<string> {
        const url = `admin/schools/${school.tenantId}/${school.schoolCode}/config`;
        return await this.authenticatedReq(url, {
            method: "PUT",
            contentType: "application/xml",
            body: config
        });
    }

    public async getSchoolConfigJson(tenantId: string, schoolCode: string): Promise<SchoolWorkloadConfig> {
        const url = `admin/schools/${tenantId}/${schoolCode}/config`;
        return await this.authenticatedGet(url);
    }

    public async putSchoolConfigJson(tenantId: string, schoolCode: string, config: SchoolWorkloadConfig): Promise<SchoolWorkloadConfig> {
        const url = `admin/schools/${tenantId}/${schoolCode}/config`;
        return await this.authenticatedPut(url, config);
    }

    public async getServiceAccountUsername(tenantId: string, schoolCode: string): Promise<string> {
        const url = `admin/schools/${tenantId}/${schoolCode}/serviceAccountCredentials/username`;
        return await this.authenticatedGet(url);
    }

    public async putServiceAccountCredentials(tenantId: string, schoolCode: string, serviceAccountCredentials: ServiceAccountCredentials): Promise<string> {
        const url = `admin/schools/${tenantId}/${schoolCode}/serviceAccountCredentials`;
        return await this.authenticatedPut(url, serviceAccountCredentials);
    }

    public async devCreateSchool(schoolCode: string, sdsSchoolId: string): Promise<void> {
        return await this.authenticatedPost(`dev/create-school/${schoolCode}/${sdsSchoolId}`, "");
    }

    public async devBuildSchool(schoolCode: string): Promise<void> {
        return await this.authenticatedPost(`dev/build-school/${schoolCode}`, "");
    }

    public async generateDevSalamanderPassword(schoolCode: string): Promise<string> {
        return await this.authenticatedPost(`dev/generate-salamander-password/${schoolCode}`, "");
    }

    public async devStartWorkloadsLoadTest(schoolCount: string): Promise<void> {
        return await this.authenticatedPost(`dev/load-test-workloads/${schoolCount}`, "");
    }

    public async devGetAuditLogs(pagingRequest: PagingRequest): Promise<PagedResult<SchoolAuditLog>> {
        return await this.authenticatedGet(`dev/auditlog${pagingRequest.toQueryParams()}`);
    }
    
    public async devBulkFetchArbor(force: boolean) : Promise<void>
    {
        return await this.authenticatedPost(`dev/bulk-fetch-arbor-direct?force=${force}`, "");
    }

    public async getSalamanderDatashareRecords(tenantId: string, schoolcode: string): Promise<SalamanderDatashareRecord[]> {
        return await this.authenticatedGet(`admin/mailroom/proxy/salamander/dataShareRecords/${tenantId}/${schoolcode}`);
    }

    public async getSalamaderDateshareItem(name: string): Promise<string> {
        return await this.authenticatedGet(`admin/mailroom/proxy/salamander/dataShareItem/${name}`);
    }

    public async triggerMailroomProcessor(tenantId: string, schoolcode: string, overrides?: Partial<MailroomSchoolConfig>): Promise<DurableFunctionHttpManagement> {
        return await this.authenticatedPost(`admin/mailroom/proxy/salamander/processor/${tenantId}/${schoolcode}/trigger`, overrides);
    }

    public async getSchoolsMailroomFunctionInstances(tenantId: string, schoolCode: string, name: string): Promise<DurableFunctionInstance[]> {
        const url = `admin/mailroom/proxy/listSchoolInstances/${tenantId}/${schoolCode}/${name}`;
        return await this.authenticatedGet(url);
    }

    public async getTenantMailroomFunctionInstances(tenantId: string, name: string): Promise<DurableFunctionInstance[]> {
        const url = `admin/mailroom/proxy/listTenantInstances/${tenantId}/${name}`;
        return await this.authenticatedGet(url);
    }

    public async getMailroomDurableFunctionInstances(queryParams: QueryObject | undefined = undefined): Promise<DurableFunctionInstance[]> {
        const url = `admin/mailroom/proxy/listInstances`;
        return await this.authenticatedGet(url, queryParams);
    }

    public async getMailroomBulkProcessorComparisonInstances(): Promise<DurableFunctionInstance[]> {
        const url = `admin/mailroom/proxy/bulkProcessorComparison`;
        return await this.authenticatedGet(url);
    }

    public async getMailroomBulkProcessorComparisonItems(instanceId: string): Promise<DurableFunctionInstance[]> {
        const url = `admin/mailroom/proxy/bulkProcessorComparison/${instanceId}/items`;
        return await this.authenticatedGet(url);
    }
    
    public async getBulkProcessorComparisonItemWithResult(bulkInstanceId: string, itemInstanceId: string): Promise<{itemInstance: DurableFunctionInstance,comparisonResult: ProcessorComparisonResult}>
    {
        const url = `admin/mailroom/proxy/bulkProcessorComparison/${bulkInstanceId}/items/${itemInstanceId}`;
        return await this.authenticatedGet(url);
    }

    public async triggerMailroomBulkProcessorComparison(input: BulkProcessorComparisonInput): Promise<DurableFunctionInstance> {
        const url = `admin/mailroom/proxy/bulkProcessorComparison`;
        return await this.authenticatedPost(url, input);
    }

    public async getProfileMailroomFunctionInstances(tenantId: string, profileId: string, names: string[]): Promise<DurableFunctionInstance[]> {
        var nameArray = names.join(",")
        const url = `admin/mailroom/proxy/listProfileInstances/${tenantId}/${profileId}/${nameArray}`;
        return await this.authenticatedGet(url);
    }

    public async getMailroomFunctionInstance(instanceId: string): Promise<DurableFunctionInstance> {
        const url = `admin/mailroom/proxy/instances/${instanceId}`;
        return await this.authenticatedGet(url);
    }

    public async purgeMailroomFunctionInstance(instanceId: string): Promise<any> {
        const url = `admin/mailroom/proxy/instances/${instanceId}/purge`;
        return await this.authenticatedGet(url);
    }

    public async getDurableFunctionInstanceOutput<T>(appName: DurableFunctionAppName, instanceId: string): Promise<T> {
        const url = `admin/${appName}/proxy/instances/${instanceId}/output`;
        return await this.authenticatedGet(url);
    }

    public async getMailroomFunctionInstanceHistory(instanceId: string): Promise<DurableFunctionHistoryEvent[]> {
        var url = `admin/mailroom/proxy/instances/${instanceId}/history`;
        return await this.authenticatedGet(url);
    }

    public async getWorkloadsFunctionInstanceHistory(instanceId: string): Promise<DurableFunctionHistoryEvent[]> {
        var url = `admin/workloads/proxy/instances/${instanceId}/history`;
        return await this.authenticatedGet(url);
    }

    public async getMailroomProcessorItems(processorItemsId: string): Promise<ProcessorItem[]> {
        var url = `admin/mailroom/proxy/processorItems/${processorItemsId}`;
        return await this.authenticatedGet(url);
    }

    public async getMailroomAutomationHistory(tenantId: string, profileId: string): Promise<MailroomAutomationHistoryRecord[]> {
        var url = `admin/mailroom/proxy/salamander/automation/${tenantId}/${profileId}/history`;
        return await this.authenticatedGet(url);
    }

    public async getAllMailroomAutomationHistoryRecords(daysToLoad: string): Promise<MailroomAutomationHistoryRecord[]> {
        var url = `admin/mailroom/proxy/salamander/automation/history`;
        return await this.authenticatedGet(url, { daysToLoad: daysToLoad });
    }

    public async getSdsCSV(processorInstanceId: string): Promise<any> {
        var url = `admin/mailroom/proxyFileResult/salamander/processor/instance/${processorInstanceId}/download`;

        return await this.authenticatedReqBlob(url, {
            method: "GET",
            contentType: "application/zip",
        });
    }

    public async getSdsCSVBlank(tenantId: string, schoolCode: string): Promise<any> {
        var url = `admin/mailroom/proxyFileResult/sds/${tenantId}/${schoolCode}/csv/blank`;

        return await this.authenticatedReqBlob(url, {
            method: "GET",
            contentType: "application/zip",
        });
    }


    public async getUploadsSdsCSV(uploadId: string): Promise<any> {
        var url = `admin/mailroom/proxyFileResult/sds/upload/${uploadId}/csv`;

        return await this.authenticatedReqBlob(url, {
            method: "GET",
            contentType: "application/zip",
        });
    }

    public async getProcessorPhotos(processorInstanceId: string): Promise<MailroomGetPhotosResult> {
        const url = `admin/mailroom/proxy/salamander/processor/instance/${processorInstanceId}/getPhotos`;
        return await this.authenticatedGet(url);
    }

    public async getProcessorPhotosZip(processorInstanceId: string): Promise<any> {
        const url = `admin/mailroom/proxyFileResult/salamander/processor/instance/${processorInstanceId}/getPhotosZip`;

        return await this.authenticatedReqBlob(url, {
            method: "GET",
            contentType: "application/zip",
        });
    }

    public async triggerPhotoInstallWorkload(tenantId: string, schoolCode: string, profileId: string): Promise<string> {
        const url = `admin/mailroom/proxy/salamander/${tenantId}/${schoolCode}/${profileId}/triggerPhotoInstall`;

        return await this.authenticatedPost(url, "");
    }

    public async compareProcessorOutputs(instanceIdA: string, instanceIdB: string): Promise<ProcessorComparisonResult> {
        const url = `admin/mailroom/proxy/salamander/processor/instance/${instanceIdA}/compareTo/${instanceIdB}`;
        return await this.authenticatedGet(url);
    }

    public async getSdsUploadStatus(uploadId: string): Promise<GetUploadStatusDTO> {
        const url = `admin/mailroom/proxy/sds/upload/${uploadId}/status`;
        return await this.authenticatedGet(url);
    }

    public async getSourceSystemDefinitions(tenantId: string): Promise<any[]> {
        const url = `admin/mailroom/proxy/sds/${tenantId}/sourceSystemDefinitions`;
        return await this.authenticatedGet(url);
    }

    public async getIndustryDataConnectors(tenantId: string): Promise<any[]> {
        const url = `admin/mailroom/proxy/sds/${tenantId}/industryDataConnectors`;
        return await this.authenticatedGet(url);
    }

    public async getIndustryDataRuns(tenantId: string): Promise<any[]> {
        const url = `admin/mailroom/proxy/sds/${tenantId}/industryDataRuns`;
        return await this.authenticatedGet(url);
    }

    public async getIndustryDataRunStatistics(tenantId: string, industryDataRunId: string): Promise<any> {
        const url = `admin/mailroom/proxy/sds/${tenantId}/industryDataRuns/${industryDataRunId}/statistics`;
        return await this.authenticatedGet(url);
    }

    public async getFileValidateOperations(tenantId: string): Promise<any[]> {
        const url = `admin/mailroom/proxy/sds/${tenantId}/validateOperations`;
        return await this.authenticatedGet(url);
    }

    public async getInboundFileFlows(tenantId: string): Promise<any[]> {
        const url = `admin/mailroom/proxy/sds/${tenantId}/inboundFileFlows`;
        return await this.authenticatedGet(url);
    }

    public async getOutboundProvisionFlowSets(tenantId: string): Promise<any[]> {
        const url = `admin/mailroom/proxy/sds/${tenantId}/outboundProvisioningFlowSets`;
        return await this.authenticatedGet(url);
    }

    public async getProvisionFlows(tenantId: string, id: string): Promise<any[]> {
        const url = `admin/mailroom/proxy/sds/${tenantId}/outboundProvisioningFlowSets/${id}/provisioningFlows`;
        return await this.authenticatedGet(url);
    }

    public async getOutboundRunsAndActivitiesForAutomation(tenantId: string, sdsKey: string): Promise<IndustryDataRunAndActivityResults[]> {
        const url = `admin/mailroom/proxy/GetOutboundRunsAndActivitiesForAutomation/${tenantId}/${sdsKey}`;
        return await this.authenticatedGet(url);
    }

    public async generateNewSalamanderPassword(tenantId: string, schoolCode: string): Promise<string> {
        const url = `salamanderSchoolConfiguration/addOrUpdate/${tenantId}/${schoolCode}`;
        return await this.authenticatedPost(url, {});
    }

    public async getSchoolsWithMailroomConfig(): Promise<MailroomSchoolConfig[]> {
        const url = `admin/mailroom/proxy/school/config`;
        return await this.authenticatedGet(url);
    }


    public async getLatestInboxUploadDates(): Promise<{ [Key: string]: string }> {
        const url = `admin/mailroom/proxy/GetLatestInboxDates`;
        return await this.authenticatedGet(url);
    }

    public async getLatestAcademicYearsInInboxForTenant(tenantId: string): Promise<{ [Key: string]: string }> {
        const url = `admin/mailroom/proxy/GetLatestAcademicYearsInInboxForTenant/${tenantId}`;
        return await this.authenticatedGet(url);
    }

    public async getLatestSdsUploadDates(): Promise<{ [Key: string]: string }> {
        const url = `admin/mailroom/proxy/GetLatestSdsUploadDates`;
        return await this.authenticatedGet(url);
    }

    public async getLatestDataUploadDateForSchool(tenantId: string, schoolCode: string): Promise<{ date?: string }> {
        const url = `admin/mailroom/proxy/school/inbox/${tenantId}/${schoolCode}/latest`;
        return await this.authenticatedGet(url);
    }

    public async getSchoolsInProfile(tenantId: string, profileId: string): Promise<{ school: School, lastDataUpload: Date }[]> {
        const url = `admin/mailroom/proxy/tenant/${tenantId}/profile/${profileId}/schools`;
        return await this.authenticatedGet(url);
    }

    public async getSchoolMailroomConfigRules(): Promise<Rule[]> {
        const url = `admin/mailroom/proxy/MailroomSchoolConfigGetRules`;
        return await this.authenticatedGet(url);
    }

    public async getSchoolMailroomConfigDto(tenantId: string, schoolCode: string): Promise<SchoolMailroomConfigDto> {
        const url = `admin/mailroom/proxy/school/${tenantId}/${schoolCode}/configDto`;
        return await this.authenticatedGet(url);
    }

    public async getMailroomSchoolConfig(tenantId: string, schoolCode: string): Promise<MailroomSchoolConfig> {
        const url = `admin/mailroom/proxy/school/${tenantId}/${schoolCode}/config`;
        return await this.authenticatedGet(url);
    }

    public async saveSchoolMailroomConfig(tenantId: string, schoolCode: string, configDto: SchoolMailroomConfigDto): Promise<any> {
        const url = `admin/mailroom/proxy/school/${tenantId}/${schoolCode}/config`;

        return await this.authenticatedPut(url, configDto);
    }

    public async deleteSchoolMailroomConfig(tenantId: string, schoolCode: string): Promise<void> {
        const url = `admin/mailroom/proxy/school/${tenantId}/${schoolCode}/config`;
        return await this.authenticatedDelete(url, null);
    }

    public async getSalamanderStrategiesFromStore(): Promise<SalamanderStrategy> {
        const url = `admin/mailroom/proxy/salamander/strategies/default`;
        return await this.authenticatedGet(url);
    }

    public async triggerAutomationUpload(tenantId: string, profileId: string): Promise<void> {
        const url = `admin/mailroom/proxy/salamander/automation/${tenantId}/${profileId}/event/start`;
        return await this.authenticatedGet(url);
    }

    public async startAutomation(tenantId: string, profileId: string): Promise<void> {
        const url = `admin/mailroom/proxy/salamander/automation/${tenantId}/${profileId}/start`;
        return await this.authenticatedGet(url);
    }

    public async getAutomationStatus(tenantId: string, schoolCode: string): Promise<string> {
        const url = `school-data/${tenantId}/${schoolCode}/automation/status`;
        return await this.authenticatedGet(url);
    }

    public async getAutomation(tenantId: string, profileId: string): Promise<DurableFunctionInstance> {
        const url = `admin/mailroom/proxy/salamander/automation/${tenantId}/profile/${profileId}`;
        return await this.authenticatedGet(url);
    }

    public async getSdsInfo(tenantId: string, sdsKey: string): Promise<SdsInfo> {
        const url = `admin/mailroom/proxy/salamander/automation/${tenantId}/sdsInfo/${sdsKey}`;
        return await this.authenticatedGet(url);
    }

    public async getAutomations(): Promise<DurableFunctionInstance[]> {
        const url = `admin/mailroom/proxy/salamander/automation`;
        return await this.authenticatedGet(url);
    }

    public async getSchoolDataReport(tenantId: string, schoolCode: string): Promise<SchoolDataReport> {
        const url = `school-data/${tenantId}/${schoolCode}/GetSchoolDataReport`;
        return await this.authenticatedGet(url);
    }

    public async terminateMailroomDurableFunction(instanceId: string): Promise<void> {
        const url = `admin/mailroom/proxy/Salamander/Automation/${instanceId}/terminate`;
        return await this.authenticatedGet(url);
    }

    public async getMailroomAnalytics(): Promise<MailroomAnalyticsResult> {
        const url = `admin/mailroom/proxy/mailroomAnalytics`;
        return await this.authenticatedGet(url);
    }

    public async triggerMailroomReportOrchestration(): Promise<DurableFunctionInstance> {
        const url = `admin/mailroom/proxy/TriggerMailroomReportOrchestration`;
        return await this.authenticatedPost(url, "");
    }

    public async downloadMailroomReport(instanceId: string): Promise<Response | undefined> {
        var url = `admin/mailroom/proxyFileResult/DownloadMailroomReport/${instanceId}`;

        return await this.authenticatedReqBlob(url, {
            method: "GET",
            contentType: "text/csv",
        });
    }

    public async downloadMailroomStrategyCsvExport(): Promise<Response | undefined> {
        var url = `admin/mailroom/proxyFileResult/GetStrategyCsvExport`;

        return await this.authenticatedReqBlob(url, {
            method: "GET",
            contentType: "text/csv",
        });
    }

    public async downloadMailroomStrategyOptionAnalysisExport(): Promise<Response | undefined> {
        const url = `admin/mailroom/proxyFileResult/GetStrategyOptionAnalysisCsvExport`;

        return await this.authenticatedReqBlob(url, {
            method: "GET",
            contentType: "text/csv",
        });
    }

    public async triggerMailroomGetAllTimingAnalysisOrchestration(): Promise<DurableFunctionInstance> {
        const url = `admin/mailroom/proxy/TriggerGetAllTimingAnalysisOrchestration`;
        return await this.authenticatedPost(url, "");
    }

    public async getMailroomTimingAnalysis(tenantId: string, sdsKey: string): Promise<any> {
        const url = `admin/mailroom/proxy/GetTimingAnalysis/${tenantId}/${sdsKey}`;
        return await this.authenticatedGet(url);
    }

    public async devGetTimingAnalysis(mailroomInstanceId: string): Promise<any> {
        return await this.authenticatedGet(`dev/timing-analysis/${mailroomInstanceId}`);
    }

    public async getSdsYearReferenceDefinitions(tenantId: string): Promise<any[]> {
        const url = `admin/mailroom/tenant/${tenantId}/SdsYearReferenceDefinitions`;
        return await this.authenticatedGet(url);
    }

    public async getRolloversForTenants(): Promise<TenantRollover[]> {
        const url = `admin/rollover/tenants`;
        return await this.authenticatedGet(url);
    }

    public async getRolloverForTenant(tenantId: string): Promise<TenantRollover> {
        const url = `admin/rollover/tenants/${tenantId}`;
        return await this.authenticatedGet(url);
    }

    public async getRolloverStateForTenant(tenantId: string): Promise<TenantRolloverState> {
        const url = `admin/rollover/tenants/${tenantId}/state`;
        return await this.authenticatedGet(url);
    }

    public async getSchoolRolloverEvents(tenantId: string): Promise<SchoolRolloverEvent[]> {
        const url = `admin/rollover/tenants/${tenantId}/schoolRolloverEvents`;
        return await this.authenticatedGet(url);
    }
    
    public async getRolloverAgentIds(): Promise<string[]>
    {
        return await this.authenticatedGet("admin/rollover/allocations/agents");
    }
    
    public async allocateTenantRollover(tenantId: string, academicYear: string,agentId: string): Promise<void>
    {
        return await this.authenticatedPut(`admin/rollover/allocations/${tenantId}/${academicYear}/${agentId}`,"");
    }
    
    public async removeTenantRolloverAllocation(tenantId: string, academicYear: string): Promise<void>
    {
        return await this.authenticatedDelete(`admin/rollover/allocations/${tenantId}/${academicYear}`,"");
    }

    public async downloadRolloverReport(): Promise<Response | undefined> {
        const url = `admin/rollover/tenants/report`;
        return await this.authenticatedReqBlob(url, {
            method: "GET",
            contentType: "text/csv",
        });
    }

    public async getRolloversStatistics(): Promise<RolloverStatisticsRow[]> {
        const url = `admin/rollover/statistics`;
        return await this.authenticatedGet(url);
    }

    public async getRolloversSummaries(types: ("mailroom" | "thirdPartySDS")[] | null,agentIds: string[] | null,isRolledOver: boolean | null): Promise<RolloverSummary[]> {
        const url = `admin/rollover/summaries`;
        return await this.authenticatedGet(url,{types: types,agentIds: agentIds,isRolledOver:isRolledOver});
    }

    public async startRolloverDisconnectForSchool(tenantId: string, schoolCode: string): Promise<void> {
        const url = `admin/rollover/tenants/${tenantId}/${schoolCode}/disconnect`;
        return await this.authenticatedPost(url, "");

    }

    public async startRolloverArchiveForSchool(tenantId: string, schoolCode: string): Promise<void> {
        const url = `admin/rollover/tenants/${tenantId}/${schoolCode}/archive`;
        return await this.authenticatedPost(url, "");
    }

    public async toggleStudentMembershipsForRollover(tenantId: string, schoolCode: string): Promise<void> {
        const url = `admin/rollover/tenants/${tenantId}/${schoolCode}/studentmembership/toggle`;
        return await this.authenticatedPost(url, "");
    }

    public async rolloverReadySchool(tenantId: string, schoolCode: string): Promise<void> {
        const url = `admin/rollover/tenants/${tenantId}/${schoolCode}/ready`;
        return await this.authenticatedPost(url, "");
    }

    public async rolloverUnreadySchool(tenantId: string, schoolCode: string): Promise<void> {
        const url = `admin/rollover/tenants/${tenantId}/${schoolCode}/unready`;
        return await this.authenticatedPost(url, "");
    }

    public async rolloverApproveUpdate(tenantId: string, schoolCode: string): Promise<void> {
        const url = `admin/rollover/tenants/${tenantId}/${schoolCode}/approveandupdaterollover`;
        return await this.authenticatedPost(url, "");
    }

    public async rolloverStartUpload(tenantId: string, schoolCode: string): Promise<void> {
        const url = `admin/rollover/tenants/${tenantId}/${schoolCode}/startRolloverUpload`;
        return await this.authenticatedPost(url, "");
    }


    public async getAdminRolloverForm(tenantId: string, schoolCode: string, academicYear: string): Promise<AdminRolloverForm> {
        const url = `admin/rollover/${tenantId}/${schoolCode}/form/${academicYear}`;
        return await this.authenticatedGet(url);
    }

    public async saveAdminRolloverForm(adminRolloverForm: AdminRolloverForm): Promise<void> {
        const url = `admin/rollover/form`;
        return await this.authenticatedPost(url, adminRolloverForm);
    }

    public async getRolloverConfirmForm(tenantId: string, schoolCode: string, academicYear: string): Promise<RolloverConfirmForm> {
        const url = `rollover/${tenantId}/${schoolCode}/form/${academicYear}`;
        return await this.authenticatedGet(url);
    }

    public async saveRolloverConfirmForm(tenantId: string, schoolCode: string, academicYear: string, rolloverConfirmForm: RolloverConfirmForm): Promise<void> {
        const url = `rollover/${tenantId}/${schoolCode}/form/${academicYear}`;
        return await this.authenticatedPost(url, rolloverConfirmForm);
    }

    public async adminImportRolloverForms(file: File): Promise<any> {
        let formData = new FormData();
        formData.append('file', file);
        return await this.authenticatedReq(`admin/rollover/import`, {
            method: "POST",
            contentType: "none",
            body: formData
        });
    }
    
    public async getBlankCommunicationSiteForm(tenantId: string, schoolCode: string): Promise<CreateSiteForm> {
        var url = `forms/create-site/${tenantId}/${schoolCode}/communication/blank`;

        return await this.authenticatedGet(url);
    }

    public async startArchiveWorkloadWithDates(tenantId: string, schoolCode: string, startDate: string, endDate: string): Promise<string> {
        const url = `/admin/archive/${tenantId}/${schoolCode}/startArchive`;
        return await this.authenticatedGet(url, { startDate, endDate });
    }

    public async startUnArchiveWorkloadWithDates(tenantId: string, schoolCode: string, startDate: string, endDate: string): Promise<string> {
        const url = `/admin/archive/${tenantId}/${schoolCode}/startUnArchive`;
        return await this.authenticatedGet(url, { startDate, endDate });
    }

    public async startDisconnectWorkloadWithDates(tenantId: string, schoolCode: string, startDate: string, endDate: string): Promise<string> {
        const url = `/admin/archive/${tenantId}/${schoolCode}/startDisconnect`;
        return await this.authenticatedGet(url, { startDate, endDate });
    }

    public async exploreGroup(tenantId: string, schoolCode: string, groupId: string): Promise<GroupExploreResult> {
        const url = `/admin/explorer/${tenantId}/${schoolCode}/group/${groupId}`;
        return await this.authenticatedGet(url);
    }

    public async exploreUser(tenantId: string, schoolCode: string, userId: string): Promise<UserExploreResult> {
        const url = `/admin/explorer/${tenantId}/${schoolCode}/user/${userId}`;
        return await this.authenticatedGet(url);
    }

    public async exploreSite(tenantId: string, schoolCode: string, siteId: string): Promise<SiteExploreResult> {
        const url = `/admin/explorer/${tenantId}/${schoolCode}/site/${siteId}`;
        return await this.authenticatedGet(url);
    }

    public async exploreDriveItem(tenantId: string, schoolCode: string, driveId: string, driveItemId: string): Promise<DriveItemExploreResult> {
        const url = `/admin/explorer/${tenantId}/${schoolCode}/drive/${driveId}/${driveItemId}`;
        return await this.authenticatedGet(url);
    }

    public async getLatestUsageSummariesForSchools(tenantId: string | undefined): Promise<IUsageResponse> {
        const queryParams = tenantId === undefined ? undefined : { tenantId: tenantId };
        return await this.authenticatedGet("/admin/usage-analytics/latestSummaries", queryParams)
    }

    public async getSchoolUsageSummaryAndDetail(tenantId: string, schoolCode: string, datasetId: string): Promise<{ summary: ISchoolUsageSummary, detail: ISchoolUsageDetail }> {
        return await this.authenticatedGet(`/admin/usage-analytics/${tenantId}/${schoolCode}/${datasetId}`)
    }

    public async getCalculationExplanationsForSchools(): Promise<{ [key in IUsageArea]: string }> {
        return await this.authenticatedGet("/admin/usage-analytics/calculationExplanations")
    }

    public async getCalculationThresholdsForSchools(): Promise<{ [key in IUsageArea]: IUsageMarkScheme }> {
        return await this.authenticatedGet("/admin/usage-analytics/markScheme")
    }

    public async getCompletedUsageDatasetsForSchool(tenantId: string, schoolCode: string): Promise<IUsageDatasetSummary[]> {
        return await this.authenticatedGet(`/admin/usage-analytics/${tenantId}/${schoolCode}/completedDatasets`);
    }

    public async getAllMisFetchHosts(): Promise<IMisFetchHost[]> {
        return await this.authenticatedGet("/admin/mis-fetch/hosts");
    }
    
    public async getMisFetchHost(tenantId: string, schoolCode: string,dataServiceType: MisDataServiceTypes): Promise<IMisFetchHost> {
        return await this.authenticatedGet(`/admin/mis-fetch/hosts/${tenantId}/${schoolCode}/${dataServiceType}`);
    }

    public async getMisFetchHostRuns(tenantId: string, schoolCode: string,dataServiceType: MisDataServiceTypes): Promise<IMisFetchRun[]> {
        return await this.authenticatedGet(`/admin/mis-fetch/hosts/${tenantId}/${schoolCode}/${dataServiceType}/runs`);
    }

    public async getSalamanderHostRunFiles(tenantId: string, schoolCode: string,dataServiceType: MisDataServiceTypes, id: string): Promise<ISalamanderHostRunBlobFile[]> {
        return await this.authenticatedGet(`/admin/mis-fetch/hosts/${tenantId}/${schoolCode}/${dataServiceType}/runs/${id}/files`);
    }

    public async getSalamanderHostRunFileContents(tenantId: string, schoolCode: string,dataServiceType: MisDataServiceTypes, id: string, name: string): Promise<string> {
        return await this.authenticatedGet(`/admin/mis-fetch/hosts/${tenantId}/${schoolCode}/${dataServiceType}/runs/${id}/files/${name}`);
    }

    public async getMisFetchRunLogs(tenantId: string, schoolCode: string, id: string): Promise<string> {
        return await this.authenticatedGet(`/admin/mis-fetch/hosts/${tenantId}/${schoolCode}/runs/${id}/logs`);
    }

    public async triggerMisFetchRun(tenantId: string, schoolCode: string,dataServiceType: MisDataServiceTypes): Promise<string> {
        return await this.authenticatedPost(`/admin/mis-fetch/hosts/${tenantId}/${schoolCode}/${dataServiceType}`, {});
    }

    public async deleteMisFetchHost(tenantId: string, schoolCode: string,dataServiceType: MisDataServiceTypes): Promise<void> {
        return await this.authenticatedDelete(`/admin/mis-fetch/hosts/${tenantId}/${schoolCode}/${dataServiceType}`, {});
    }

    public async getAllHostedSalamanderConfigs(): Promise<IHostedSalamanderConfig[]> {
        return await this.authenticatedGet(`/admin/hosted-salamander/configs`);
    }

    public async getHostedSalamanderConfig(tenantId: string, schoolCode: string): Promise<IHostedSalamanderConfig | null> {
        return await this.authenticatedGet(`/admin/hosted-salamander/configs/${tenantId}/${schoolCode}`);
    }

    public async addOrUpdateHostedSalamanderConfig(hostedSalamanderConfig: IHostedSalamanderConfig): Promise<any> {
        return await this.authenticatedPost(`/admin/hosted-salamander/configs`, hostedSalamanderConfig);
    }

    public async deleteHostedSalamanderConfig(tenantId: string, schoolCode: string) {
        return await this.authenticatedDelete(`/admin/hosted-salamander/configs/${tenantId}/${schoolCode}`, {});
    }
    
    public async getTenantManagersForTenant(tenantId: string): Promise<TenantManager[]> {
        return await this.authenticatedGet(`tenant-managers`, { tenantId });
    }

    public async createTenantManagerRecord(tenantManagerRecord: TenantManager): Promise<void> {
        return await this.authenticatedPost(`tenant-managers`, tenantManagerRecord);
    }

    public async deleteTenantManager(tenantManagerRecord: TenantManager): Promise<void> {
        return await this.authenticatedDelete(`tenant-managers`, tenantManagerRecord);
    }
    public async getMisConnectionDetails(tenantId: string, schoolCode: string): Promise<Dictionary<IMisConnectionDetailsResult>> {
        return await this.authenticatedGet(`/admin/mis-connection-details/${tenantId}/${schoolCode}`);
    }

    public async getBlankMisConnectionDetails(tenantId: string, schoolCode: string, misName: string): Promise<IMisConnectionDetailsResult> {
        return await this.authenticatedGet(`/admin/mis-connection-details/${tenantId}/${schoolCode}/${misName}/blank`);
    }

    public async putMisConnectionDetails(tenantId: string, schoolCode: string, misName: string, putOperation: IMisConnectionDetailsPutOperation): Promise<IMisConnectionDetailsResult> {
        return await this.authenticatedPut(`/admin/mis-connection-details/${tenantId}/${schoolCode}/${misName}`, putOperation);
    }

    public async deleteMisConnectionDetails(tenantId: string, schoolCode: string, misName: string): Promise<void> {
        return await this.authenticatedDelete(`/admin/mis-connection-details/${tenantId}/${schoolCode}/${misName}`, {});
    }

    public async getMisNames(tenantId: string, schoolCode: string): Promise<string[]> {
        return await this.authenticatedGet(`/admin/mis-connection-details/${tenantId}/${schoolCode}/mis-names`);
    }

    public async getFreshdeskAvailability(): Promise<{ available: boolean }> {
        return await this.authenticatedGet(`admin/workloads/proxy/getFreshdeskAvailability`)
    }

    public async getFreshdeskSyncOrchestration(): Promise<DurableFunctionInstance | null> {
        return await this.authenticatedGet(`admin/workloads/proxy/getFreshdeskSyncOrchestration`)
    }

    public async startFreshdeskSyncOrchestration(): Promise<void> {
        return await this.authenticatedPost(`admin/workloads/proxy/startFreshdeskSyncOrchestration`, "");
    }

    public async terminateFreshdeskSyncOrchestration(): Promise<void> {
        return await this.authenticatedPost(`admin/workloads/proxy/terminateFreshdeskSyncOrchestration`, "");
    }

    public async mailroomStartOrchestration(orchestrationName: string): Promise<{ instanceId: string }> {
        return await this.authenticatedPost(`admin/mailroom/proxy/StartOrchestration/${orchestrationName}`, "");
    }

    public async getAdminAppStatus(): Promise<ICDBAppStatus> {
        return await this.authenticatedGet(`GetAppStatus`)
    }

    public async getWorkloadsAppStatus(): Promise<ICDBAppStatus> {
        return await this.authenticatedGet(`admin/workloads/proxy/GetAppStatus`)
    }

    public async getMailroomAppStatus(): Promise<ICDBAppStatus> {
        return await this.authenticatedGet(`admin/mailroom/proxy/GetAppStatus`)
    }

    public async getContainerImageNames(): Promise<string[]> {
        return await this.authenticatedGet(`admin/container-image-tags/image-names`);
    }

    public async getContainerImageTagDefault(imageName: string): Promise<string> {
        return await this.authenticatedGet(`admin/container-image-tags/${doubleEncodeURIComponent(imageName)}/default`);
    }

    public async putContainerImageTagDefault(imageName: string, newDefaultTag: string): Promise<void> {
        await this.authenticatedPut(`admin/container-image-tags/${doubleEncodeURIComponent(imageName)}/default/${newDefaultTag}`, "");
    }

    public async getSchoolContainerImageTagOverrides(imageName: string): Promise<ISchoolContainerImageTagOverride[]> {
        return await this.authenticatedGet(`admin/container-image-tags/${doubleEncodeURIComponent(imageName)}/school-overrides`);
    }

    public async patchSchoolContainerImageTagOverrides(imageName: string, req: any): Promise<IContainerImageTagOverridePatch> {
        return await this.authenticatedPatch(`admin/container-image-tags/${doubleEncodeURIComponent(imageName)}/school-overrides`, req);
    }
    
    public async getTotpStatusForTenant(tenantId: string): Promise<TotpStatus> {
        return await this.authenticatedGet(`admin/totp/${tenantId}/status`);
    }

    public async getTotpTokenForTenant(tenantId: string): Promise<string> {
        return await this.authenticatedGet(`admin/totp/${tenantId}/token`);
    }

    public async putTotpSecretForTenant(tenantId: string,secret: string): Promise<void> {
        return await this.authenticatedReq(`admin/totp/${tenantId}/secret`, {
            body: JSON.stringify(secret),
            method: "PUT"
        });
    }

    public async clearWorkloadTenantLocks(): Promise<void> {
        return await this.authenticatedGet(`admin/workloads/proxy/clearTenantLocks`)
    }
}